import React, { useState, useEffect } from "react";
import LayoutAdmin from "../../components/layout/LayoutAdmin";
import { FiSearch } from "react-icons/fi";
import apiService from "../../actions/apiService";

// Helper function to format relative time (e.g., "about 3 hours ago")
const formatRelativeTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return diffInMinutes === 1
      ? "about 1 minute ago"
      : `about ${diffInMinutes} minutes ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return diffInHours === 1
      ? "about 1 hour ago"
      : `about ${diffInHours} hours ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return diffInDays === 1
      ? "about 1 day ago"
      : `about ${diffInDays} days ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return diffInWeeks === 1
      ? "about 1 week ago"
      : `about ${diffInWeeks} weeks ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return diffInMonths === 1
      ? "about 1 month ago"
      : `about ${diffInMonths} months ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return diffInYears === 1
    ? "about 1 year ago"
    : `about ${diffInYears} years ago`;
};

// Helper function to get admin user display name
const getAdminDisplayName = (user) => {
  if (!user) return "Admin user";
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  if (user.email) {
    return user.email.split("@")[0];
  }
  return "Admin user";
};

const AdminReports = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page] = useState(1);
  const [limit] = useState(20);

  useEffect(() => {
    const fetchAdminActionLogs = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiService.get("/admin/action-logs", {
          page,
          limit,
        });

        if (response.success) {
          setNotifications(response.data?.notifications || []);
        } else {
          setError(response.error || "Failed to fetch admin action logs");
        }
      } catch (err) {
        console.error("Error fetching admin action logs:", err);
        setError("Failed to fetch admin action logs");
      } finally {
        setLoading(false);
      }
    };

    fetchAdminActionLogs();
  }, [page, limit]);

  return (
    <LayoutAdmin>
      {/* Header */}
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative mb-8 z-[10]">
        <div className="flex px-5 items-center justify-between">
          <div>
            <h2 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
              Admin action logs
            </h2>
            <p className="text-small-price opacity-50 mt-4 md:text-small-price text-white lg:text-[#1E1E1E] !font-normal">
              Timestamped history of admin actions
            </p>
          </div>
        </div>
      </div>

      <div className="px-5 lg:px-0 lg:ps-5 relative">
        <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
          <h4 className="md:text-big-price-d !font-[500] text-big-price font-rubik text-esdarkGray mb-4">
            Recent activity
          </h4>

          {loading && (
            <div className="ps-4 py-8 text-center text-gray-500">
              Loading admin action logs...
            </div>
          )}

          {error && (
            <div className="ps-4 py-8 text-center text-red-500">{error}</div>
          )}

          {!loading && !error && notifications.length === 0 && (
            <div className="ps-4 py-8 text-center text-gray-500">
              No admin action logs found
            </div>
          )}

          {!loading && !error && notifications.length > 0 && (
            <div className="ps-4">
              {notifications.map((notification) => (
                <div key={notification.id} className="py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="">
                      <div className="flex items-start gap-2">
                        <span className="text-esblue">
                          <FiSearch className="w-6 h-6" />
                        </span>
                        <div>
                          <p className="text-[16px] font-[600] text-[#1E1E1E] opacity-75">
                            {notification.title}
                          </p>
                        </div>
                      </div>
                      <p className="text-[16px] opacity-50 mt-2 text-[#000025] font-[400]">
                        by {getAdminDisplayName(notification.user)}
                      </p>
                    </div>
                    <div className="text-[12px] font-[500] text-[#000025] opacity-50 whitespace-nowrap">
                      {formatRelativeTime(notification.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </LayoutAdmin>
  );
};

export default AdminReports;
